import React, { useState } from 'react';
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    Text,
    Spinner,
    Center,
    Alert,
    AlertIcon,
    AlertDescription,
    HStack,
    VStack,
    Flex,
    IconButton,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { MdLocalGasStation } from 'react-icons/md';
import dayjs from 'dayjs';
import type {
    FuelConsumptionRecord,
    FuelConsumptionSource,
    FuelConsumptionSummary,
} from '../types';

interface FuelConsumptionTableProps {
    records: FuelConsumptionRecord[];
    summary: FuelConsumptionSummary | null;
    date: string;
    loading: boolean;
    error: string | null;
}

// Lacak's refuel events are liters with 2 decimals; below 1 L is rounding, not a discrepancy.
const DIFFERENCE_TOLERANCE_L = 1;

const formatNumber = (num: number | null | undefined, decimals = 2) => {
    if (num === null || num === undefined) return '-';
    return new Intl.NumberFormat('id-ID', {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
    }).format(num);
};

const SOURCE_BADGE: Record<FuelConsumptionSource, { label: string; colorScheme: string }> = {
    both: { label: 'Cocok', colorScheme: 'green' },
    odoo_only: { label: 'Hanya Odoo', colorScheme: 'yellow' },
    lacak_only: { label: 'Hanya Lacak', colorScheme: 'red' },
};

const differenceColor = (diff: number | null) => {
    if (diff === null) return 'gray.400';
    if (Math.abs(diff) < DIFFERENCE_TOLERANCE_L) return 'green.600';
    return diff > 0 ? 'red.600' : 'orange.600';
};

const differenceBg = (diff: number | null) => {
    if (diff === null || Math.abs(diff) < DIFFERENCE_TOLERANCE_L) return 'green.100';
    return diff > 0 ? 'red.100' : 'orange.100';
};

const HeaderInfoBox: React.FC<{ label: string; value: string; bg?: string }> = ({
    label,
    value,
    bg = '#ededed',
}) => (
    <HStack spacing={4}>
        <Box
            bg={bg}
            p={2}
            borderRadius="lg"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
        >
            <MdLocalGasStation size={25} color="black" />
        </Box>
        <Box>
            <Text color="black" fontSize="sm">{label}</Text>
            <Text color="black" fontWeight="bold">{value}</Text>
        </Box>
    </HStack>
);

const GroupTh: React.FC<{ colSpan: number; label: string; bordered?: boolean }> = ({
    colSpan,
    label,
    bordered = true,
}) => (
    <Th
        colSpan={colSpan}
        py={2}
        px={3}
        borderLeft={bordered ? '1px solid' : undefined}
        borderColor="whiteAlpha.300"
        borderBottom="none"
    >
        <Text whiteSpace="nowrap" color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
            {label}
        </Text>
    </Th>
);

const ColTh: React.FC<{ label: string; numeric?: boolean }> = ({ label, numeric = true }) => (
    <Th py={2} px={3} textAlign={numeric ? 'right' : 'left'}>
        <Text whiteSpace="nowrap" color="white" fontSize="2xs" fontWeight="normal">
            {label}
        </Text>
    </Th>
);

const NumCell: React.FC<{ value: number | null | undefined; bold?: boolean; color?: string }> = ({
    value,
    bold = false,
    color,
}) => (
    <Td isNumeric textAlign="right">
        <Text fontSize="xs" fontWeight={bold ? 'bold' : 'normal'} color={color ?? 'gray.700'}>
            {formatNumber(value)}
        </Text>
    </Td>
);

export const FuelConsumptionTable: React.FC<FuelConsumptionTableProps> = ({
    records,
    summary,
    date,
    loading,
    error,
}) => {
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

    const toggleRow = (key: string) => {
        setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    if (loading) {
        return (
            <Center py={10}>
                <Spinner size="xl" color="green.500" thickness="4px" />
            </Center>
        );
    }

    if (error) {
        return (
            <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    if (!summary || records.length === 0) {
        return (
            <Alert status="info" borderRadius="md">
                <AlertIcon />
                <AlertDescription>Tidak ada data untuk ditampilkan</AlertDescription>
            </Alert>
        );
    }

    const totalDifference = summary.qty_done - summary.lacak_refuel_l;

    return (
        <VStack spacing={0} align="stretch">
            {/* Summary header card */}
            <Box borderRadius="lg" boxShadow="sm" overflow="hidden" bg="white">
                <Flex direction={{ base: 'column', lg: 'row' }} align="stretch">
                    <Box bg="#e7efdf" p={4} flex="2.5">
                        <Text color="black" fontWeight="bold">SOLAR | {dayjs(date).format('DD MMM YYYY')}</Text>
                        <Text color="black" fontSize="sm">
                            {summary.units_both} unit cocok · {summary.units_odoo_only} hanya Odoo · {summary.units_lacak_only} hanya Lacak
                        </Text>
                    </Box>
                    <Box bg="white" p={4} flex="1">
                        <HeaderInfoBox label="Diminta (MR)" value={`${formatNumber(summary.qty_requested)} L`} />
                    </Box>
                    <Box bg="white" p={4} flex="1">
                        <HeaderInfoBox label="Dikeluarkan (Odoo)" value={`${formatNumber(summary.qty_done)} L`} />
                    </Box>
                    <Box bg="white" p={4} flex="1">
                        <HeaderInfoBox label="Pengisian (Lacak)" value={`${formatNumber(summary.lacak_refuel_l)} L`} />
                    </Box>
                    <Box bg="white" p={4} flex="1">
                        <HeaderInfoBox
                            label="Selisih"
                            value={`${formatNumber(totalDifference)} L`}
                            bg={differenceBg(totalDifference)}
                        />
                    </Box>
                </Flex>
            </Box>

            {/* Data table */}
            <Box overflowX="auto" bg="white" borderRadius="lg" boxShadow="sm" mt={3}>
                <Table variant="unstyled" size="sm">
                    <Thead>
                        <Tr bg="#4A5A33">
                            <GroupTh colSpan={3} label="Informasi Unit" bordered={false} />
                            <GroupTh colSpan={2} label="Odoo (Liter)" />
                            <GroupTh colSpan={3} label="Lacak" />
                            <GroupTh colSpan={1} label="Selisih" />
                        </Tr>
                        <Tr bg="#4A5A33">
                            <ColTh label="Unit" numeric={false} />
                            <ColTh label="Plat Lacak" numeric={false} />
                            <ColTh label="Status" numeric={false} />
                            <ColTh label="Diminta" />
                            <ColTh label="Dikeluarkan" />
                            <ColTh label="Pengisian (L)" />
                            <ColTh label="Pemakaian (L)" />
                            <ColTh label="Jarak (km)" />
                            <ColTh label="Dikeluarkan - Pengisian (L)" />
                        </Tr>
                    </Thead>

                    <Tbody>
                        {records.map((record, index) => {
                            const rowKey = `${record.source}-${record.unit_id ?? record.vehicle_id ?? index}`;
                            const isExpanded = expandedRows[rowKey] ?? false;
                            const badge = SOURCE_BADGE[record.source];
                            const hasLines = record.lines.length > 0;

                            return (
                                <React.Fragment key={rowKey}>
                                    <Tr
                                        bg={isExpanded ? 'green.50' : 'white'}
                                        _hover={{ bg: isExpanded ? 'green.50' : 'gray.50' }}
                                        borderTop="1px solid"
                                        borderColor="blackAlpha.100"
                                    >
                                        <Td>
                                            <HStack spacing={2}>
                                                {hasLines ? (
                                                    <IconButton
                                                        aria-label="Expand unit"
                                                        icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                                                        size="xs"
                                                        variant="ghost"
                                                        onClick={() => toggleRow(rowKey)}
                                                    />
                                                ) : (
                                                    <Box w="24px" />
                                                )}
                                                <Text fontSize="xs" fontWeight="bold" color="gray.700">
                                                    {record.unit_code ?? '-'}
                                                </Text>
                                            </HStack>
                                        </Td>
                                        <Td>
                                            <Text fontSize="xs" color="gray.700">{record.license_plate ?? '-'}</Text>
                                        </Td>
                                        <Td>
                                            <Badge colorScheme={badge.colorScheme} variant="subtle" fontSize="xs">
                                                {badge.label}
                                            </Badge>
                                        </Td>
                                        <NumCell value={record.qty_requested} bold />
                                        <NumCell value={record.qty_done} bold />
                                        <NumCell value={record.lacak_refuel_l} />
                                        <NumCell value={record.lacak_usage_l} />
                                        <NumCell value={record.lacak_distance_km} />
                                        <NumCell
                                            value={record.difference_l}
                                            bold
                                            color={differenceColor(record.difference_l)}
                                        />
                                    </Tr>

                                    {isExpanded && record.lines.map((line, lineIndex) => (
                                        <Tr key={`${rowKey}-${lineIndex}`} bg="white" _hover={{ bg: 'gray.50' }}>
                                            <Td pl={10}>
                                                <Text fontSize="xs" color="gray.600">{line.mr ?? '-'}</Text>
                                            </Td>
                                            <Td>
                                                <Text fontSize="xs" color="gray.600">{line.siv ?? '-'}</Text>
                                            </Td>
                                            <Td>
                                                <Text fontSize="2xs" color="gray.500">
                                                    {line.siv_date ? dayjs(line.siv_date).format('DD MMM YYYY') : '-'}
                                                    {line.warehouse ? ` · ${line.warehouse}` : ''}
                                                    {line.spbu_date ? ` · SPBU ${dayjs(line.spbu_date).format('DD MMM HH:mm')}` : ''}
                                                </Text>
                                            </Td>
                                            <NumCell value={line.qty_requested} />
                                            <NumCell value={line.qty_done} />
                                            <Td colSpan={4} />
                                        </Tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </Tbody>
                </Table>
            </Box>
        </VStack>
    );
};
